import React, { useState, useEffect } from 'react';
import { Bell, MessageCircle, Star, Heart, Share2, AlertTriangle, Check, X, ChevronRight, HelpCircle, ExternalLink, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Mapeamento de ícones por tipo
const NOTIFICATION_ICONS = {
  new_comment: MessageCircle,
  new_review: Star,
  new_like: Heart,
  new_favorite: Heart,
  new_share: Share2,
  new_news: Bell,
  new_evento: Bell,
  new_acao: Bell,
  new_capacitacao: Bell,
  new_financeiro: Bell,
  new_parceria: Bell,
  comment_reply: MessageCircle,
  comment_moderation: AlertTriangle,
  report_received: AlertTriangle,
  report_resolved: Check,
  content_reported: AlertTriangle,
  content_removed: X,
  account_warning: AlertTriangle,
  account_suspended: X,
  business_approved: Check,
  business_rejected: X,
  business_suspended: AlertTriangle,
  // Solicitações por categoria / Feed
  solicitacao_nova: HelpCircle,
  solicitacao_expirada: AlertTriangle,
  feed_novo_post: Megaphone,
  coletivo_nova_solicitacao: Bell,
  coletivo_adesao_aprovada: Check,
  coletivo_adesao_rejeitada: X,
  coletivo_novo_evento: Bell,
};

// Cores por tipo
const NOTIFICATION_COLORS = {
  new_comment: 'bg-blue-100 text-blue-600',
  new_review: 'bg-yellow-100 text-yellow-600',
  new_like: 'bg-pink-100 text-pink-600',
  new_favorite: 'bg-pink-100 text-pink-600',
  new_share: 'bg-purple-100 text-purple-600',
  new_news: 'bg-indigo-100 text-indigo-600',
  new_evento: 'bg-purple-100 text-purple-600',
  new_acao: 'bg-teal-100 text-teal-600',
  new_capacitacao: 'bg-blue-100 text-blue-600',
  new_financeiro: 'bg-green-100 text-green-600',
  new_parceria: 'bg-orange-100 text-orange-600',
  comment_reply: 'bg-blue-100 text-blue-600',
  comment_moderation: 'bg-orange-100 text-orange-600',
  report_received: 'bg-orange-100 text-orange-600',
  report_resolved: 'bg-green-100 text-green-600',
  content_reported: 'bg-orange-100 text-orange-600',
  content_removed: 'bg-red-100 text-red-600',
  account_warning: 'bg-yellow-100 text-yellow-600',
  account_suspended: 'bg-red-100 text-red-600',
  business_approved: 'bg-green-100 text-green-600',
  business_rejected: 'bg-red-100 text-red-600',
  business_suspended: 'bg-orange-100 text-orange-600',
  solicitacao_nova: 'bg-amber-100 text-amber-700',
  solicitacao_expirada: 'bg-gray-100 text-gray-600',
  feed_novo_post: 'bg-purple-100 text-purple-700',
  coletivo_nova_solicitacao: 'bg-purple-100 text-purple-700',
  coletivo_adesao_aprovada: 'bg-green-100 text-green-600',
  coletivo_adesao_rejeitada: 'bg-red-100 text-red-600',
  coletivo_novo_evento: 'bg-purple-100 text-purple-700',
};

// Badge contador
export const NotificationBadge = ({ className = '' }) => {
  const { token } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    
    const fetchCount = async () => {
      try {
        const res = await api.get('/v2/notifications/unread-count');
        setCount(res.data.count);
      } catch (err) {
        console.error('Error fetching notification count:', err);
      }
    };

    fetchCount();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  if (count === 0) return null;

  return (
    <span className={`absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center ${className}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

// Item de notificação
const NotificationItem = ({ notification, onMarkRead }) => {
  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
  const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-gray-100 text-gray-600';
  
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return date.toLocaleDateString('pt-BR');
  };

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }
  };

  // ---------- Card especial para solicitação nova ----------
  if (notification.type === 'solicitacao_nova') {
    const payload = notification.payload || {};
    const expira = payload.expira_em ? new Date(payload.expira_em) : null;
    const msRest = expira ? Math.max(0, expira.getTime() - Date.now()) : 0;
    const hRest = msRest / 36e5;
    let timer = '';
    if (expira) {
      if (hRest < 1) timer = `em ${Math.max(1, Math.round(msRest / 6e4))} min`;
      else if (hRest < 24) timer = `em ${Math.round(hRest)}h`;
      else timer = `em ${Math.ceil(hRest / 24)}d`;
    }

    async function abrirWp(e) {
      e.preventDefault();
      if (!notification.is_read) onMarkRead(notification.id);
      if (payload.solicitacao_id) {
        try {
          await api.post(`/v2/solicitacoes/${payload.solicitacao_id}/clique-whatsapp`, {
            negocio_id: notification.business_id,
          });
        } catch { /* silencioso */ }
      }
      if (payload.link_whatsapp) {
        window.open(payload.link_whatsapp, '_blank', 'noopener,noreferrer');
      }
    }

    return (
      <div
        className={`p-4 border-b border-gray-100 ${!notification.is_read ? 'bg-amber-50/40' : ''}`}
        data-testid={`notification-item-${notification.id}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            {!notification.is_read ? <div className="w-2 h-2 rounded-full bg-amber-500" /> : <div className="w-2 h-2" />}
          </div>
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
              {notification.title}
            </p>
            {payload.texto_solicitacao && (
              <p className="text-sm text-gray-700 mt-1 italic">&ldquo;{payload.texto_solicitacao}&rdquo;</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {payload.link_whatsapp ? (
                <button
                  onClick={abrirWp}
                  data-testid={`notif-wp-${notification.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Entrar em contato via WhatsApp <ExternalLink className="w-3 h-3" />
                </button>
              ) : (
                <span className="text-xs text-gray-500">Link de contato indisponível</span>
              )}
              {timer && (
                <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Responda logo — expira {timer}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">{timeAgo(notification.created_at)}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determinar link de destino
  let linkTo = '#';
  if (notification.related_entity_type === 'comment' && notification.business_id) {
    linkTo = `/negocio/${notification.business_id}#comentarios`;
  } else if (notification.related_entity_type === 'report') {
    linkTo = `/meus-negocios/alertas`;
  } else if (notification.business_id) {
    linkTo = `/negocio/${notification.business_id}`;
  }

  return (
    <Link
      to={linkTo}
      onClick={handleClick}
      className={`flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      }`}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Indicador de não lido */}
      <div className="flex-shrink-0 mt-1">
        {!notification.is_read ? (
          <div className="w-2 h-2 rounded-full bg-[var(--primary)]" />
        ) : (
          <div className="w-2 h-2" />
        )}
      </div>

      {/* Ícone */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {timeAgo(notification.created_at)}
        </p>
      </div>

      {/* Seta */}
      <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
    </Link>
  );
};

// Lista de notificações
export const NotificationList = ({ limit = 10, showViewAll = true }) => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const res = await api.get('/v2/notifications', { params: { limit } });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, limit]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.patch(`/v2/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/v2/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)] mx-auto"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="p-8 text-center">
        <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">Nenhuma notificação</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      {unreadCount > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-sm text-gray-600">
            {unreadCount} não lida{unreadCount > 1 ? 's' : ''}
          </span>
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-[var(--primary)] hover:underline"
          >
            Marcar todas como lidas
          </button>
        </div>
      )}

      {/* Lista */}
      <div className="divide-y divide-gray-100">
        {notifications.map(notification => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkRead={handleMarkRead}
          />
        ))}
      </div>

      {/* Ver todas */}
      {showViewAll && (
        <Link
          to="/meus-negocios/alertas"
          className="block text-center py-3 text-sm text-[var(--primary)] hover:bg-gray-50 border-t border-gray-100"
        >
          Ver todas as notificações
        </Link>
      )}
    </div>
  );
};

// Dropdown de notificações
export const NotificationDropdown = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Dropdown */}
      <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Notificações</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <NotificationList limit={5} showViewAll={true} />
        </div>
      </div>
    </>
  );
};

export default NotificationList;
